import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, Plus, Minus, ShoppingBag, Send, WifiOff, RefreshCw, Ticket, X, UtensilsCrossed, ChevronRight, CheckCircle2 } from "lucide-react";
import GuestLayout from "@/components/GuestLayout";
import { createOrder, getSettings, getGuestOTPStatus, sendGuestOTP, verifyGuestOTP, getCategorySettings } from "@/lib/api";
import api from "@/lib/api";
import { 
  getPropertyId, 
  getPropertySlug,
  getCart, 
  updateCartQuantity, 
  removeFromCart, 
  clearCart, 
  getCartTotal,
  getGuestInfo,
  setGuestInfo,
  savePendingOrder,
  getPendingOrder,
  clearPendingOrder
} from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Standard FSSAI Indian Dietary Indicator (Veg / Non-Veg)
const DietaryBadge = ({ isVeg }) => {
  if (isVeg) {
    return (
      <span className="inline-flex items-center justify-center w-3.5 h-3.5 border-[1.5px] border-emerald-600 rounded-[3px] p-[1px] bg-white flex-shrink-0" title="Vegetarian">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-3.5 h-3.5 border-[1.5px] border-[#b91c1c] rounded-[3px] p-[1px] bg-white flex-shrink-0" title="Non-Vegetarian">
      <span className="w-0 h-0 border-l-[3px] border-l-transparent border-r-[3px] border-r-transparent border-b-[5px] border-b-[#b91c1c]"></span>
    </span>
  );
};

export default function Cart() {
  const navigate = useNavigate();
  const propertyId = getPropertyId();
  const propertySlug = getPropertySlug();
  const [cart, setCart] = useState([]);
  const [settings, setSettings] = useState(null);
  const [isOffline, setIsOffline] = useState(false);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);

  const savedInfo = getGuestInfo();
  const [formData, setFormData] = useState({
    guest_name: savedInfo.name || "",
    room_number: savedInfo.room || "",
    whatsapp: savedInfo.whatsapp || "",
    notes: "",
  });

  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [calling, setCalling] = useState(false);

  // Coupon State
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  useEffect(() => {
    setCart(getCart());
    
    // Check for pending order (crash recovery)
    const pending = getPendingOrder();
    if (pending) {
      setPendingOrder(pending);
      toast.info("You have a pending order. Would you like to retry?", {
        duration: 8000,
      });
    }
    
    const fetchSettings = async () => {
      try {
        const data = await getSettings(propertyId);
        setSettings(data);
        setIsOffline(data?.food_enabled === false);
        setNetworkError(false);
      } catch (error) {
        console.error("Error fetching settings:", error);
        if (error.isNetworkError) {
          setNetworkError(true);
        }
      }
    };
    fetchSettings();
  }, [propertyId]);

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  
  let currentDiscountAmount = 0;
  if (appliedCoupon) {
    if (appliedCoupon.discount_type === 'percent') {
      currentDiscountAmount = (appliedCoupon.discount_value / 100.0) * subtotal;
    } else {
      currentDiscountAmount = appliedCoupon.discount_value;
    }
    currentDiscountAmount = Math.min(currentDiscountAmount, subtotal);
  }
  const rawTotal = subtotal > 0 ? Math.max(subtotal - currentDiscountAmount, 0) : 0;
  const total = Math.round(rawTotal * 100) / 100;
  const formattedTotal = Number(total).toFixed(2).replace(/\.00$/, '');

  useEffect(() => {
    if (cart.length === 0 && appliedCoupon) {
      setAppliedCoupon(null);
      setCouponCode("");
    }
  }, [cart.length, appliedCoupon]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError("");
    try {
      const res = await api.post("/coupons/validate", {
        code: couponCode,
        property_id: propertyId,
        order_amount: subtotal
      });
      setAppliedCoupon({
        code: couponCode.toUpperCase(),
        discount_type: res.data.discount_type,
        discount_value: res.data.discount_value,
        discount_amount: res.data.discount_amount
      });
      toast.success(res.data.message || "Coupon applied successfully!");
    } catch (error) {
      console.error("Coupon validation error:", error);
      const backendError = error.response?.data?.detail;
      const errorMsg = Array.isArray(backendError) ? backendError[0] : backendError;
      setCouponError(errorMsg || "Invalid coupon code");
      toast.error(errorMsg || "Invalid coupon code");
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
    toast.info("Coupon removed");
  };

  const handleUpdateQuantity = (itemId, quantity) => {
    const newCart = updateCartQuantity(itemId, quantity, propertyId);
    setCart(newCart);
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const handleRemove = (itemId) => {
    const newCart = removeFromCart(itemId, propertyId);
    setCart(newCart);
    window.dispatchEvent(new Event("cartUpdated"));
    toast.success("Item removed from order");
  };

  const handleRetryConnection = async () => {
    setNetworkError(false);
    try {
      const data = await getSettings(propertyId);
      setSettings(data);
      toast.success("Connection restored!");
    } catch (error) {
      if (error.isNetworkError) {
        setNetworkError(true);
      }
    }
  };

  const handleRetryPendingOrder = async () => {
    if (!pendingOrder) return;
    
    setSubmitting(true);
    try {
      const createdOrder = await createOrder(pendingOrder);
      
      clearPendingOrder();
      setPendingOrder(null);
      clearCart(propertyId);
      setCart([]);
      window.dispatchEvent(new Event("cartUpdated"));
      
      toast.success("Order placed! Our team will confirm within 5 minutes via WhatsApp.");
      setShowOrderForm(false);
      navigate(`/${getPropertySlug()}?property=${propertyId}`);
    } catch (error) {
      console.error("Error retrying order:", error);
      if (error.isNetworkError) {
        toast.error("Still no connection. Your order is saved locally.");
      } else {
        toast.error("There was a technical issue. Please ask reception to place your order manually.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearPendingOrder = () => {
    clearPendingOrder();
    setPendingOrder(null);
    toast.success("Pending order cleared");
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();

    if (settings && settings.food_enabled === false) {
      toast.error("Food ordering is currently closed or paused for this property. Please contact reception.");
      return;
    }

    if (!formData.guest_name || !formData.room_number || !formData.whatsapp) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      // 1. Check Verification Status
      const status = await getGuestOTPStatus(formData.whatsapp);
      
      if (status.is_verified) {
        // Already verified today, proceed directly
        await finalizeOrder();
      } else {
        // Need verification, send OTP
        const sendResult = await sendGuestOTP(formData.whatsapp, propertyId);
        if (sendResult.is_verified) {
          // Handled by backend returning is_verified directly from send OTP
          await finalizeOrder();
        } else if (sendResult.success) {
          setShowOrderForm(false);
          setShowOTPDialog(true);
          toast.success(sendResult.message || "Verification code sent!");
        } else {
          toast.error(sendResult.error || "Failed to send verification code. Please try again.");
        }
      }
    } catch (error) {
      console.error("Verification check failed:", error);

      if (error.isNetworkError) {
        const orderData = {
          guest_name: formData.guest_name,
          room_number: formData.room_number,
          whatsapp: formData.whatsapp,
          items: cart.map((item) => ({ item_id: item.id, name: item.name, quantity: item.quantity, price: item.price })),
          total: total,
          property_id: propertyId,
          notes: formData.notes,
          ...(appliedCoupon && { coupon_code: appliedCoupon.code, discount_amount: currentDiscountAmount })
        };
        setShowOrderForm(false);
        setPendingOrder(orderData);
        savePendingOrder(orderData);
      }
      const backendError = error.response?.data?.detail;
      const errorMsg = Array.isArray(backendError) ? backendError[0] : backendError;
      toast.error(
        errorMsg || "We're experiencing a technical issue. Please contact reception for a manual order. Sorry for the inconvenience! 🙏",
        { duration: 8000 }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyAndOrder = async (e) => {
    e.preventDefault();
    if (otpValue.length !== 4) {
      toast.error("Please enter the 4-digit code sent to your WhatsApp");
      return;
    }

    setVerifying(true);
    try {
      const result = await verifyGuestOTP(formData.whatsapp, otpValue);
      if (result.success) {
        toast.success("Verified!");
        setShowOTPDialog(false);
        await finalizeOrder();
      } else {
        toast.error("Invalid code. Please try again.");
      }
    } catch (error) {
      console.error("Verification failed:", error);

      if (error.isNetworkError) {
        const orderData = {
          guest_name: formData.guest_name,
          room_number: formData.room_number,
          whatsapp: formData.whatsapp,
          items: cart.map((item) => ({ item_id: item.id, name: item.name, quantity: item.quantity, price: item.price })),
          total: total,
          property_id: propertyId,
          notes: formData.notes,
          ...(appliedCoupon && { coupon_code: appliedCoupon.code, discount_amount: currentDiscountAmount })
        };
        setShowOrderForm(false);
        setPendingOrder(orderData);
        savePendingOrder(orderData);
      }
      const backendError = error.response?.data?.detail;
      const errorMsg = Array.isArray(backendError) ? backendError[0] : backendError;
      toast.error(errorMsg || "Verification failed. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    setResending(true);
    try {
      const sendResult = await sendGuestOTP(formData.whatsapp, propertyId, "sms");
      if (sendResult.success) {
        toast.success(sendResult.message || "New code sent via SMS!");
      } else {
        toast.error(sendResult.error || "Failed to resend code");
      }
    } catch (error) {
      console.error("Failed to resend OTP:", error);
      toast.error("Failed to resend code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleCallOTP = async () => {
    setCalling(true);
    try {
      const sendResult = await sendGuestOTP(formData.whatsapp, propertyId, "call");
      toast.success(sendResult.message || "Calling your phone now!");
    } catch (error) {
      console.error("Failed to request voice OTP:", error);
      toast.error("Failed to place call. Please try again.");
    } finally {
      setCalling(false);
    }
  };

  const finalizeOrder = async () => {
    if (settings && settings.food_enabled === false) {
      toast.error("Food ordering is currently closed or paused for this property. Please contact reception.");
      return;
    }

    // Save guest info immediately
    setGuestInfo({
      name: formData.guest_name,
      room: formData.room_number,
      whatsapp: formData.whatsapp,
    });

    // Prepare order data
    const orderData = {
      guest_name: formData.guest_name,
      room_number: formData.room_number,
      whatsapp: formData.whatsapp,
      items: cart.map((item) => ({
        item_id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
      })),
      total: total,
      property_id: propertyId,
      notes: formData.notes,
      ...(appliedCoupon && {
        coupon_code: appliedCoupon.code,
        discount_amount: currentDiscountAmount
      })
    };

    // Save as pending order before attempting (crash recovery)
    savePendingOrder(orderData);

    try {
      // Create order in backend
      const createdOrder = await createOrder(orderData);

      // Success - clear pending order
      clearPendingOrder();

      // Clear cart
      clearCart(propertyId);
      setCart([]);
      window.dispatchEvent(new Event("cartUpdated"));

      toast.success("Order placed! Koko is notifying the kitchen 🍳");
      setShowOrderForm(false);
      navigate(`/${getPropertySlug()}?property=${propertyId}`);
      
    } catch (error) {
      console.error("Error placing order:", error);
      const backendError = error.response?.data?.detail;
      const errorMsg = Array.isArray(backendError) ? backendError[0] : backendError;
      toast.error(
        errorMsg || "We're experiencing a technical issue. Please contact reception for a manual order. Sorry for the inconvenience! 🙏",
        { duration: 8000 }
      );
      if (error.isNetworkError) {
        setShowOrderForm(false);
        setPendingOrder(orderData);
      }
    }
  };


  
  if (isOffline) {
    return (
      <GuestLayout title="Cart" showBack={true}>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6">
          <UtensilsCrossed className="w-16 h-16 text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-[#264653] mb-4">Kitchen is Closed</h2>
          <p className="text-[#6B705C] max-w-md mb-8">
            Our kitchen is currently closed for online orders. Please reach out to the front desk to pre-order or inquire about availability.
          </p>
          <Button onClick={() => navigate(`/${getPropertySlug()}?property=${propertyId}`)} className="btn-primary">
            Back to Home
          </Button>
        </div>
      </GuestLayout>
    );
  }


  return (
    <GuestLayout>
      <div className="min-h-screen bg-[#FBF9F6] pb-24" data-testid="cart-page">
        {/* Network Error Banner */}
        {networkError && (
          <div className="bg-red-500 text-white px-4 py-3 flex items-center justify-between" data-testid="network-error-banner">
            <div className="flex items-center gap-2">
              <WifiOff className="w-5 h-5" />
              <span className="font-medium text-sm">Poor Network Connection</span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-red-600 h-8"
              onClick={handleRetryConnection}
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Retry
            </Button>
          </div>
        )}

        {/* Pending Order Recovery Banner */}
        {pendingOrder && (
          <div className="bg-amber-500 text-white px-4 py-3 shadow-md" data-testid="pending-order-banner">
            <div className="max-w-xl mx-auto flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs md:text-sm">
                <ShoppingBag className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium">Unsent order found (₹{pendingOrder.total})</span>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="bg-white text-amber-700 hover:bg-amber-50 text-xs font-bold h-7"
                  onClick={handleRetryPendingOrder}
                  disabled={submitting}
                >
                  {submitting ? "Sending..." : "Retry"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-white hover:bg-amber-600 text-xs h-7"
                  onClick={handleClearPendingOrder}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Premium Header */}
        <div className="bg-white border-b border-[#E8E4D9]/60 px-4 md:px-8 py-4 sticky top-0 z-30 shadow-sm backdrop-blur-md bg-white/90">
          <div className="max-w-xl mx-auto flex items-center justify-between">
            <button
              onClick={() => navigate(`/food?property=${propertyId}`)}
              className="flex items-center gap-2 text-[#264653]/80 hover:text-[#2A9D8F] transition-colors font-medium text-sm"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Menu</span>
            </button>
            <div className="text-right">
              <h1 
                className="font-bold text-lg text-[#264653] leading-tight"
                style={{ fontFamily: "'Quicksand', sans-serif" }}
                data-testid="page-title"
              >
                Review Order
              </h1>
              <p className="text-[11px] text-gray-500 font-medium">
                {cart.length} {cart.length === 1 ? "item" : "items"} selected
              </p>
            </div>
          </div>
        </div>

        {/* Content Container */}
        <div className="max-w-xl mx-auto p-4 md:p-6">
          {cart.length === 0 ? (
            /* Empty State */
            <div className="text-center py-16 px-6 bg-white rounded-3xl border border-[#E8E4D9]/60 shadow-sm mt-4">
              <div className="w-20 h-20 mx-auto rounded-3xl bg-[#2A9D8F]/10 flex items-center justify-center text-[#2A9D8F] mb-4">
                <UtensilsCrossed className="w-10 h-10" />
              </div>
              <h2 
                className="font-bold text-xl text-[#264653] mb-2"
                style={{ fontFamily: "'Quicksand', sans-serif" }}
              >
                Your order is empty
              </h2>
              <p className="text-gray-500 text-sm mb-6 max-w-xs mx-auto leading-relaxed">
                Discover freshly made local dishes, snacks, and drinks on our menu.
              </p>
              <Button
                onClick={() => navigate(`/food?property=${propertyId}`)}
                className="rounded-full bg-[#2A9D8F] hover:bg-[#218377] text-white px-6 py-2.5 font-bold text-sm shadow-md"
                data-testid="browse-menu"
              >
                Explore Cafe Menu
              </Button>
            </div>
          ) : (
            <>
              {/* Cart Items List */}
              <div className="space-y-3 mb-6">
                {cart.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 p-3.5 bg-white rounded-2xl border border-[#E8E4D9]/60 shadow-sm"
                    data-testid={`cart-item-${item.id}`}
                  >
                    {/* Dish Image */}
                    {item.image_url ? (
                      <div className="w-16 h-16 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100">
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&q=80&w=400";
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 flex-shrink-0 rounded-xl bg-[#2A9D8F]/10 flex items-center justify-center text-[#2A9D8F]">
                        <ShoppingBag className="w-6 h-6 opacity-40" />
                      </div>
                    )}

                    {/* Dish Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <DietaryBadge isVeg={item.is_vegetarian !== false} />
                        <h3 
                          className="font-bold text-[#264653] text-sm truncate"
                          style={{ fontFamily: "'Quicksand', sans-serif" }}
                        >
                          {item.name}
                        </h3>
                      </div>
                      <p className="text-xs text-gray-500 font-medium mt-0.5">
                        ₹{item.price} each
                      </p>
                      <p className="text-[#2A9D8F] font-extrabold text-sm mt-0.5" style={{ fontFamily: "'Quicksand', sans-serif" }}>
                        ₹{item.price * item.quantity}
                      </p>
                    </div>

                    {/* Quantity Stepper & Remove */}
                    <div className="flex items-center gap-1.5">
                      <div className="flex items-center gap-1 bg-[#2A9D8F]/10 rounded-xl p-1 border border-[#2A9D8F]/20">
                        <button
                          onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                          className="w-7 h-7 rounded-lg bg-white flex items-center justify-center text-[#2A9D8F] hover:bg-[#2A9D8F] hover:text-white transition-colors shadow-sm active:scale-90"
                          data-testid={`cart-decrease-${item.id}`}
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="w-6 text-center font-bold text-xs text-[#2A9D8F]" data-testid={`cart-quantity-${item.id}`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                          className="w-7 h-7 rounded-lg bg-white flex items-center justify-center text-[#2A9D8F] hover:bg-[#2A9D8F] hover:text-white transition-colors shadow-sm active:scale-90"
                          data-testid={`cart-increase-${item.id}`}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      <button
                        onClick={() => handleRemove(item.id)}
                        className="w-8 h-8 rounded-xl flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        data-testid={`cart-remove-${item.id}`}
                        title="Remove dish"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Receipt Summary Card */}
              <div className="bg-white rounded-3xl p-5 shadow-sm border border-[#E8E4D9]/60 mb-6 space-y-4">
                {/* Coupon Input Box */}
                <div className="pb-4 border-b border-gray-100">
                  <Label className="text-[#264653] font-bold text-xs uppercase tracking-wider mb-2 block">
                    Have a Promo Code?
                  </Label>
                  {!appliedCoupon ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input 
                          placeholder="e.g. MONKEY10" 
                          value={couponCode} 
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          className="uppercase font-mono text-xs rounded-xl bg-[#FBF9F6] border-gray-200 focus:border-[#2A9D8F]"
                        />
                        <Button 
                          onClick={handleApplyCoupon} 
                          disabled={couponLoading || !couponCode.trim()}
                          className="bg-[#2A9D8F] hover:bg-[#218377] text-white text-xs font-bold rounded-xl px-4"
                        >
                          {couponLoading ? "Checking..." : "Apply"}
                        </Button>
                      </div>
                      {couponError && <span className="text-red-500 text-xs block">{couponError}</span>}
                      <p className="text-[11px] text-gray-400 italic">
                        Ask reception about our review rewards and Instagram specials!
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-emerald-50 text-emerald-800 p-2.5 rounded-xl border border-emerald-200">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        <span className="font-bold text-xs font-mono">{appliedCoupon.code} applied</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-xs text-emerald-700">-₹{currentDiscountAmount.toFixed(2)}</span>
                        <button onClick={handleRemoveCoupon} className="text-emerald-700/60 hover:text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bill Itemization */}
                <div className="space-y-2 text-xs md:text-sm text-gray-600">
                  <div className="flex justify-between items-center">
                    <span>Items Total</span>
                    <span className="font-semibold text-[#264653]">₹{Number(subtotal).toFixed(2).replace(/\.00$/, '')}</span>
                  </div>
                  
                  {appliedCoupon && (
                    <div className="flex justify-between items-center text-emerald-600 font-semibold">
                      <span>Promo Discount</span>
                      <span>-₹{Number(currentDiscountAmount).toFixed(2).replace(/\.00$/, '')}</span>
                    </div>
                  )}


                  <div className="flex justify-between items-center text-gray-500 text-xs">
                    <span>Cafe Packaging & Service</span>
                    <span className="font-medium text-gray-700">Free (Complimentary)</span>
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                    <span className="font-bold text-base text-[#264653]" style={{ fontFamily: "'Quicksand', sans-serif" }}>
                      To Pay
                    </span>
                    <span className="font-extrabold text-xl text-[#2A9D8F]" style={{ fontFamily: "'Quicksand', sans-serif" }}>
                      ₹{formattedTotal}
                    </span>
                  </div>
                </div>

                {/* Checkout Trigger Button */}
                <Button
                  className="w-full h-12 rounded-2xl bg-[#2A9D8F] hover:bg-[#218377] text-white font-bold text-sm shadow-md flex items-center justify-center gap-2 active:scale-95 transition-all mt-2"
                  onClick={() => setShowOrderForm(true)}
                  data-testid="place-order"
                >
                  <span>Confirm Order</span>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Order Form Modal */}
        <Dialog open={showOrderForm} onOpenChange={setShowOrderForm}>
          <DialogContent className="sm:max-w-md bg-white rounded-3xl p-6 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle 
                className="text-xl font-bold text-[#264653]"
                style={{ fontFamily: "'Quicksand', sans-serif" }}
              >
                Where should we serve?
              </DialogTitle>
              <DialogDescription className="text-gray-500 text-xs">
                Provide your room or table details for prompt delivery
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handlePlaceOrder} className="space-y-3.5 mt-2" data-testid="order-form">
              <div>
                <Label htmlFor="guest_name" className="text-xs font-bold text-gray-700">Your Name *</Label>
                <Input
                  id="guest_name"
                  value={formData.guest_name}
                  onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                  className="mt-1 text-sm rounded-xl bg-[#FBF9F6] border-gray-200 focus:border-[#2A9D8F]"
                  placeholder="e.g. Rahul Sharma"
                  required
                  data-testid="order-input-guest-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="room_number" className="text-xs font-bold text-gray-700">Room / Bed *</Label>
                  <Input
                    id="room_number"
                    value={formData.room_number}
                    onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
                    className="mt-1 text-sm rounded-xl bg-[#FBF9F6] border-gray-200 focus:border-[#2A9D8F]"
                    placeholder="e.g. Dorm 3 / Bed A"
                    required
                    data-testid="order-input-room-number"
                  />
                </div>

                <div>
                  <Label htmlFor="whatsapp" className="text-xs font-bold text-gray-700">WhatsApp *</Label>
                  <Input
                    id="whatsapp"
                    value={formData.whatsapp}
                    onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                    className="mt-1 text-sm rounded-xl bg-[#FBF9F6] border-gray-200 focus:border-[#2A9D8F]"
                    placeholder="9876543210"
                    required
                    data-testid="order-input-whatsapp"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notes" className="text-xs font-bold text-gray-700">Cooking Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="mt-1 text-xs rounded-xl bg-[#FBF9F6] border-gray-200 focus:border-[#2A9D8F] min-h-[70px]"
                  placeholder="e.g. Extra spicy, no sugar in tea, please bring napkins..."
                  data-testid="order-input-notes"
                />
              </div>

              <div className="pt-2">
                <div className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-xl mb-4 border border-gray-100">
                  <span className="text-gray-600 text-xs font-semibold">Total Payable</span>
                  <span className="font-extrabold text-lg text-[#2A9D8F]">₹{total}</span>
                </div>
                
                <Button
                  type="submit"
                  className="w-full h-11 rounded-xl bg-[#2A9D8F] hover:bg-[#218377] text-white font-bold text-sm shadow-md"
                  disabled={submitting}
                  data-testid="submit-order"
                >
                  {submitting ? "Processing..." : "Confirm & Place Order"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* OTP Verification Dialog */}
        <Dialog open={showOTPDialog} onOpenChange={setShowOTPDialog}>
          <DialogContent className="sm:max-w-md bg-white rounded-3xl p-6">
            <DialogHeader>
              <DialogTitle 
                className="text-xl font-bold text-[#264653]"
                style={{ fontFamily: "'Quicksand', sans-serif" }}
              >
                Verify Contact Number
              </DialogTitle>
              <DialogDescription className="text-gray-500 text-xs">
                We've sent a 4-digit code to <strong>{formData.whatsapp}</strong> to confirm your room order.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleVerifyAndOrder} className="space-y-5 mt-3">
              <div className="flex justify-center">
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={4}
                  value={otpValue}
                  onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, ""))}
                  className="text-center text-3xl tracking-[0.5em] font-bold h-14 w-52 rounded-2xl bg-[#FBF9F6] border-2 border-[#2A9D8F]/30 focus:border-[#2A9D8F]"
                  placeholder="••••"
                  required
                  autoFocus
                />
              </div>

              <div className="text-center flex items-center justify-center gap-3 text-xs">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={resending || calling}
                  className="text-[#2A9D8F] font-semibold hover:underline disabled:opacity-50"
                >
                  {resending ? "Sending..." : "Resend SMS"}
                </button>
                <span className="text-gray-300">•</span>
                <button
                  type="button"
                  onClick={handleCallOTP}
                  disabled={resending || calling}
                  className="text-[#2A9D8F] font-semibold hover:underline disabled:opacity-50"
                >
                  {calling ? "Calling..." : "Call me"}
                </button>
              </div>

              <Button
                type="submit"
                className="w-full h-11 rounded-xl bg-[#2A9D8F] hover:bg-[#218377] text-white font-bold text-sm"
                disabled={verifying}
              >
                {verifying ? "Verifying..." : "Verify & Complete Order"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </GuestLayout>
  );
}
